import { memo } from 'react'
import { useAppSelector } from '@/store/store-hooks'
import { useMedSummaryParent } from '@/medical-billing-summaries/parent-context'
import { EditableEntryField } from '../editable-field-config'
import { useStartEditField } from '../hooks/useStartEditField'
import { isCellBeingEdited } from '../utils/edit-helpers'
import EditFieldPopover from './EditFieldPopover'

interface CommentCellProps {
  comment: string
  entryId: string
}

/**
 * Comment Cell
 *
 * Allows users to add and edit a comment for a billing summary entry.
 */
export default memo(function CommentCell({ comment, entryId }: CommentCellProps) {
  const { id: parentId, readOnly } = useMedSummaryParent()

  // Hook for initiating edit mode
  const startEdit = useStartEditField({ entryId, fieldKey: EditableEntryField.COMMENT, originalValue: comment ?? '' })

  // Check if this cell is currently being edited (boolean selector avoids re-renders on every keystroke)
  const isEditing = useAppSelector((state) => isCellBeingEdited(state.medSummaryEntryEditState.activeEdit, parentId, entryId, EditableEntryField.COMMENT))

  // In view mode, show only the first line and preserve spacing
  // Desired UX: Multi-line content is hidden until edit mode (similar to Google Sheets)
  const displayComment = comment ? comment.split('\n')[0] : ''

  // Read-only viewers with no comment see a blank cell — "Add a comment..." is a no-op for them
  if (readOnly && !comment) {
    return <div className="pl-4 py-[10.5px]" />
  }

  return (
    <EditFieldPopover isOpen={isEditing} fieldKey={EditableEntryField.COMMENT}>
      <div className="pl-4 py-[10.5px] hover:bg-brand-neutral-50" onClick={startEdit}>
        {!comment ? (
          <span className="text-sm text-brand-neutral-500 italic">Add a comment...</span>
        ) : (
          <span className="text-sm overflow-hidden whitespace-pre block">{displayComment}</span>
        )}
      </div>
    </EditFieldPopover>
  )
})
